import { DataGrid, GridColumns, GridRowParams } from '@material-ui/data-grid';
import React, { ChangeEvent, useCallback, useContext, useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { IoMdRemoveCircle } from 'react-icons/io';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { ThemeContext } from 'styled-components';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import FormDefault from '~/components/FormDefault';
import { InputText, InputSelect, Loja } from '~/components/NovosInputs';
import { BuscaProduto } from '~/components/NovosInputs';
import Search from '~/components/Search';
import Separator from '~/components/Separator';
import { LojaContext } from '~/context/loja';
import api from '~/services/api';

import { ButtonRow } from '../Cliente/styles';
import { Container } from './styles';
import { ProdutosProps } from './types';
import { yupResolver } from '@hookform/resolvers/yup';
import { schema } from './validations';
import DefaultLoader from '~/components/DefaultLoader';
import { CustomButtonNovo } from '~/components/Buttons/CustomButtonNovo';

const tiposChecklist = [
  {
    value: 0,
    label: 'Outros',
  },
  {
    value: 1,
    label: 'Carga',
  },
];

const MySwal = withReactContent(Swal);

const CheckList: React.FC = () => {
  const { loja } = useContext(LojaContext);
  const [lojas, setLojas] = useState<number>(loja.cod_loja);
  const [loader, setLoader] = useState<boolean>(false);
  const [loaderTable, setLoaderTable] = useState<boolean>(false);
  const [produtos, setProdutos] = useState<ProdutosProps[]>([]);
  const [resetCampo, setResetCampo] = useState<boolean>(false);
  const [isUpdate, setUpdate] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [codLista, setCodlista] = useState<number | undefined>(undefined);

  const { colors } = useContext(ThemeContext);

  const {
    register,
    control,
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    reValidateMode: 'onSubmit',
  });

  const resetFormData = () => {
    setResetCampo(true);
    reset({
      cod_loja: {
        value: loja.cod_loja,
        label: `${loja.des_loja} (${loja.des_cidade})`,
      },
      descricao: '',
      tipo: null,
    });
    setLojas(loja.cod_loja);
    setUpdate(false);
    setValue('produto', []);
    setProdutos([]);
    setCurrentPage(0);
  };

  const handleAddProduto = useCallback(
    (itens: any[]) => {
      setLoaderTable(true);
      if (itens.length === 0) {
        setLoaderTable(false);
        return toast.warning('Produtos não encontrados na loja selecionada');
      }

      const formattedItems = itens.map((item) => ({
        key: parseInt(item.cod_produto.replace(/^0+/, ''), 10),
        cod_produto: item.cod_produto,
        des_produto: item.des_produto,
      }));

      const uniqueItems = formattedItems.filter(
        (item, index, self) =>
          index === self.findIndex((t) => t.key === item.key),
      );

      const existingProducts = new Set(
        produtos.map((p) => parseInt(p.id.replace(/^0+/, ''), 10)),
      );
      const updatedProducts: ProdutosProps[] = [...produtos];

      let isDuplicate = false;

      uniqueItems.forEach((item) => {
        const { key, cod_produto, des_produto } = item;

        if (cod_produto) {
          if (existingProducts.has(key)) {
            isDuplicate = true;
          } else {
            existingProducts.add(key);
            updatedProducts.push({ id: cod_produto, descricao: des_produto });
          }
        }
      });

      if (isDuplicate) {
        toast.warning('Item já adicionado no checklist');
      }

      updatedProducts.sort((a, b) => a.descricao.localeCompare(b.descricao));

      setValue('produto', updatedProducts);
      setProdutos(updatedProducts);
      setLoaderTable(false);
      const buscaProduto = document.getElementsByName('busca_por');
      if (buscaProduto.length > 0) {
        buscaProduto[0].click();
        buscaProduto[0].focus();
      }
      setTimeout(() => {
        setValue('busca_por', {
          value: null,
          label: 'Selecione...',
        });
      }, 50);
    },
    [produtos, setValue],
  );

  const handleRemoveProduto = useCallback(
    (id: string) => {
      MySwal.fire({
        title: `Check-List`,
        text: 'Deseja remover este item do check-list?',

        showCancelButton: true,
        confirmButtonColor: '#07289e',
        cancelButtonColor: '#ff7b7b',
        confirmButtonText: 'Sim',
        cancelButtonText: 'Não',
      }).then(async (result) => {
        if (result.isConfirmed) {
          setLoaderTable(true);
          const prods = produtos.filter((p: { id: string }) => p.id !== id);
          setValue('produto', prods);
          setProdutos(prods);
          setLoaderTable(false);
        }
        setLoaderTable(false);
      });
    },
    [produtos, setValue],
  );

  const handleRemoveAllProdutos = useCallback(() => {
    MySwal.fire({
      title: `Check-List`,
      text: 'Deseja remover todos os itens do check-list?',

      showCancelButton: true,
      confirmButtonColor: '#07289e',
      cancelButtonColor: '#ff7b7b',
      confirmButtonText: 'Sim',
      cancelButtonText: 'Não',
    }).then(async (result) => {
      if (result.isConfirmed) {
        setValue('produto', []);
        setProdutos([]);
      }
    });
  }, [setValue]);

  const tabColumns: GridColumns = [
    {
      field: 'id',
      headerName: 'Código',
      width: 150,
      cellClassName: 'super-app-theme--cell',
      headerClassName: 'super-app-theme--header',
      align: 'left',
    },
    {
      field: 'descricao',
      headerName: 'Descrição',
      flex: 1,
      minWidth: 200,
      cellClassName: 'super-app-theme--cell',
      headerClassName: 'super-app-theme--header',
    },
    {
      field: '',
      headerName: 'Ação',
      sortable: false,
      width: 100,
      headerAlign: 'center',
      align: 'center',
      cellClassName: 'super-app-theme--cell actions-cell',
      headerClassName: 'super-app-theme--header actions-cell',
      renderCell: (params: { row: any }) => {
        const { row } = params;
        const onRemove = () => {
          handleRemoveProduto(row.id);
        };
        return (
          <>
            <ButtonRow type="button" onClick={onRemove}>
              <IoMdRemoveCircle size={20} style={{ color: '#e63c3c' }} />
            </ButtonRow>
          </>
        );
      },
    },
  ];

  const onRowClick = async (param: GridRowParams) => {
    setLoader(true);
    const { row } = param;
    const {
      tipo_da_lista,
      des_lista,
      cod_lista,
      cod_loja,
      des_loja,
      des_loja_cidade,
    } = row;
    setValue('descricao', des_lista);
    setValue('cod_loja', {
      value: cod_loja,
      label: `${des_loja} (${des_loja_cidade})`,
    });
    setLojas(cod_loja);
    setCodlista(cod_lista);

    const findTipo = tiposChecklist.find((t) => t.label === tipo_da_lista);
    if (findTipo) {
      setValue('tipo', findTipo);
    }

    const { data } = await api.get(`/checklist/produtos/${cod_lista}`);
    if (data.success) {
      const formattedProdutos = data.data.flatMap((p: any) =>
        p.cod_loja === cod_loja
          ? [{ ...p, id: p.cod_produto, descricao: p.des_produto }]
          : [],
      );
      setValue('produto', formattedProdutos);
      setProdutos(formattedProdutos);
    }
    setUpdate(true);
    setCurrentPage(0);
    setShowSearch(false);
    setLoader(false);
  };

  const onCancel = () => {
    resetFormData();
    setShowSearch(true);
  };

  const onDelete = async () => {
    const res = await api.delete(`/checklist/${codLista}`);
    const { message } = res.data;
    onCancel();
    return toast.success(message);
  };

  const onNew = () => {
    resetFormData();
    setUpdate(false);
    setShowSearch(false);
  };

  const onSave = handleSubmit(async (checkForm) => {
    setLoader(true);
    try {
      const { produto, cod_loja, tipo, descricao } = checkForm;
      if (!produto || produto.length <= 0) {
        toast.warning('Ao menos um produto deve ser informado.');
        setLoader(false);
        return;
      }

      const formattedProducts = produto.map((p: ProdutosProps) => ({
        cod_produto: p.id,
        descricao: p.descricao,
      }));

      const dataForm = {
        produtos: formattedProducts,
        descricao: descricao.trim(),
        tipo_lista: tipo?.value,
        cod_loja: cod_loja.value,
      };

      if (isUpdate) {
        const { data } = await api.put(`/checklist/${codLista}`, dataForm);

        if (data.success) {
          toast.success(data.message);
          resetFormData();
          setShowSearch(true);
        }
        setLoader(false);
      } else {
        const { data } = await api.post('/checklist', dataForm);

        if (data.success) {
          toast.success(data.message);
          resetFormData();
        }
      }
    } finally {
      setLoader(false);
    }
  });

  if (loader) {
    return (
      <Container>
        <DefaultLoader />
      </Container>
    );
  }
  return (
    <Container>
      {showSearch && (
        <Search newData={onNew} onRowClick={onRowClick} codTela={31} />
      )}

      {!showSearch && (
        <FormDefault
          codTela={31}
          title="Check-List"
          codigoRegistro={[
            {
              des_campo: 'Código do Registro',
              value: codLista,
            },
          ]}
          onCancel={onCancel}
          onClearFields={resetFormData}
          onDelete={onDelete}
          onNew={onNew}
          onReturnSearch={onCancel}
          onSave={async () => {
            await onSave();
          }}
          isUpdate={isUpdate}
        >
          <Col sm={12} md={12}>
            <Row>
              <Col sm={12} md={10}>
                <InputText
                  label="Descrição"
                  maxLength={50}
                  placeholder="Informe a Descrição"
                  name="descricao"
                  register={register}
                  control={control}
                  isError={!!errors.descricao}
                  onChange={(ev: ChangeEvent<HTMLInputElement>) => {
                    const val = ev.target.value.toLocaleUpperCase();
                    setValue('descricao', val.trimStart());
                  }}
                />
              </Col>

              <Col sm={12} md={2}>
                <InputSelect
                  label="Tipo"
                  name="tipo"
                  options={tiposChecklist}
                  placeholder="Informe o Tipo"
                  register={register}
                  control={control}
                  changeSelected={(value) => {
                    setValue('tipo', value);
                  }}
                  isError={!!errors.tipo}
                />
              </Col>
              <Col md={12} sm={12}>
                <Separator labelText="Pesquisar Produtos" />
              </Col>
              {/* Buscar produtos */}
              <Col md={12} sm={12}>
                <Loja
                  label="Loja"
                  name="cod_loja"
                  placeholder="Selecione..."
                  register={register}
                  control={control}
                  isError={false}
                  getValues={getValues}
                  retriever={lojas}
                  disabled={false}
                  changeSelected={(ev) => {
                    setValue('cod_loja', ev);
                    setLojas(ev.value);
                  }}
                />
              </Col>
              <Col sm={12} md={12}>
                <BuscaProduto
                  label="Buscar por"
                  placeholder="Selecione..."
                  name="busca_por"
                  register={register}
                  isError={!!errors.busca_por}
                  control={control}
                  buscaNasLojas={lojas}
                  buscaItensInativos={false}
                  clearCampoBusca={resetCampo}
                  customOptions={{
                    hideBuscarPor: true,
                    buscarPor: [
                      'Produto',
                      'Departamento',
                      'Fornecedor',
                      'NCM',
                      'Marca',
                    ],
                    buscaPadrao: 'Produto',
                    showSelecionarItensContendo: true,
                  }}
                  getProduct={(selected: any) => {
                    handleAddProduto(selected);
                    setResetCampo(false);
                  }}
                  setValue={setValue}
                  style={{ width: '100%' }}
                />
              </Col>
              {/* tabela de produtos similares */}
              <Col
                sm={12}
                md={12}
                className="dataTable"
                style={{ height: 400, width: '100%', marginTop: '30px' }}
              >
                {loaderTable && (
                  <div className="w-100 h-100 d-flex justify-content-center align-items-center">
                    <ClipLoader color={colors.primary} />
                  </div>
                )}
                {!loaderTable && (
                  <DataGrid
                    page={currentPage}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                    }}
                    rows={produtos}
                    columns={tabColumns}
                    pageSize={5}
                    disableColumnFilter
                    disableColumnSelector
                    disableColumnMenu
                    disableSelectionOnClick
                    localeText={{
                      noRowsLabel:
                        'Nenhum registro encontrado com o critério informado',
                    }}
                  />
                )}
              </Col>
              <div className="d-flex justify-content-end my-3 px-4">
                <CustomButtonNovo
                  onClick={handleRemoveAllProdutos}
                  disabled={produtos.length === 0}
                  label="REMOVER TODOS ITENS"
                  outline
                  width="185px"
                />
              </div>
            </Row>
          </Col>
        </FormDefault>
      )}
    </Container>
  );
};

export default CheckList;
